import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../hooks/useAuth';
import '../../styles/AiAdmin.css';

const AiAdmin = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('prompts');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Prompts state
  const [prompts, setPrompts] = useState([]);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [editPromptText, setEditPromptText] = useState('');
  const [showAddPrompt, setShowAddPrompt] = useState(false);
  const [newPrompt, setNewPrompt] = useState({ feature_key: '', prompt_type: 'system', prompt_text: '' });

  // Configs state
  const [configs, setConfigs] = useState([]);
  const [editingConfig, setEditingConfig] = useState(null);
  const [editConfigValues, setEditConfigValues] = useState({});

  // Rate limits state
  const [rateLimits, setRateLimits] = useState([]);

  const headers = { Authorization: `Bearer ${token}` };
  const baseUrl = process.env.REACT_APP_SERVER_BASE_URL;

  const showMessage = (type, text) => {
    if (type === 'error') { setError(text); setSuccess(''); }
    else { setSuccess(text); setError(''); }
    setTimeout(() => { setError(''); setSuccess(''); }, 4000);
  };

  // ── Fetch functions ──────────────────────────────────────────────────────

  const fetchPrompts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseUrl}/hospital-portal/api/ai/admin/prompts`, { headers });
      setPrompts(res.data);
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to load prompts');
    } finally { setLoading(false); }
  }, [baseUrl, token]);

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseUrl}/hospital-portal/api/ai/admin/configs`, { headers });
      setConfigs(res.data);
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to load configs');
    } finally { setLoading(false); }
  }, [baseUrl, token]);

  const fetchRateLimits = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseUrl}/hospital-portal/api/ai/admin/rate-limits`, { headers });
      setRateLimits(res.data);
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to load rate limits');
    } finally { setLoading(false); }
  }, [baseUrl, token]);

  useEffect(() => {
    if (activeTab === 'prompts') fetchPrompts();
    else if (activeTab === 'configs') fetchConfigs();
    else if (activeTab === 'rate-limits') fetchRateLimits();
  }, [activeTab]);

  // ── Prompt handlers ──────────────────────────────────────────────────────

  const handleEditPrompt = (prompt) => {
    setEditingPrompt(prompt);
    setEditPromptText(prompt.prompt_text);
  };

  const handleSavePrompt = async () => {
    if (!editingPrompt) return;
    try {
      await axios.put(
        `${baseUrl}/hospital-portal/api/ai/admin/prompts/${editingPrompt.id}`,
        { prompt_text: editPromptText },
        { headers }
      );
      showMessage('success', 'Prompt updated (new version created)');
      setEditingPrompt(null);
      fetchPrompts();
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to update prompt');
    }
  };

  const handleAddPrompt = async () => {
    const { feature_key, prompt_type, prompt_text } = newPrompt;
    if (!feature_key || !prompt_type || !prompt_text) {
      showMessage('error', 'All fields are required');
      return;
    }
    try {
      await axios.post(
        `${baseUrl}/hospital-portal/api/ai/admin/prompts`,
        { feature_key, prompt_type, prompt_text },
        { headers }
      );
      showMessage('success', 'Prompt created');
      setShowAddPrompt(false);
      setNewPrompt({ feature_key: '', prompt_type: 'system', prompt_text: '' });
      fetchPrompts();
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to create prompt');
    }
  };

  // ── Config handlers ──────────────────────────────────────────────────────

  const handleEditConfig = (config) => {
    setEditingConfig(config);
    setEditConfigValues({
      enabled: config.enabled,
      model: config.model,
      temperature: config.temperature,
      max_output_tokens: config.max_output_tokens,
      rate_limit_per_minute: config.rate_limit_per_minute,
      rate_limit_per_day: config.rate_limit_per_day,
      lockout_duration_minutes: config.lockout_duration_minutes,
    });
  };

  const handleSaveConfig = async () => {
    if (!editingConfig) return;
    try {
      await axios.put(
        `${baseUrl}/hospital-portal/api/ai/admin/configs/${editingConfig.id}`,
        editConfigValues,
        { headers }
      );
      showMessage('success', 'Config updated');
      setEditingConfig(null);
      fetchConfigs();
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to update config');
    }
  };

  // ── Rate limit handlers ──────────────────────────────────────────────────

  const handleUnlockUser = async (lockoutId) => {
    if (!window.confirm('Unlock this user?')) return;
    try {
      await axios.delete(
        `${baseUrl}/hospital-portal/api/ai/admin/rate-limits/${lockoutId}`,
        { headers }
      );
      showMessage('success', 'User unlocked');
      fetchRateLimits();
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to unlock user');
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="ai-admin">
      <h2>AI Administration</h2>

      {error && <div className="ai-admin-msg ai-admin-error">{error}</div>}
      {success && <div className="ai-admin-msg ai-admin-success">{success}</div>}

      <div className="ai-admin-tabs">
        <button className={activeTab === 'prompts' ? 'active' : ''} onClick={() => setActiveTab('prompts')}>
          Prompts
        </button>
        <button className={activeTab === 'configs' ? 'active' : ''} onClick={() => setActiveTab('configs')}>
          Feature Configs
        </button>
        <button className={activeTab === 'rate-limits' ? 'active' : ''} onClick={() => setActiveTab('rate-limits')}>
          Rate Limits
        </button>
      </div>

      {loading && <div className="ai-admin-loading">Loading...</div>}

      {/* ── Prompts Tab ──────────────────────────────────────────────────── */}
      {activeTab === 'prompts' && !loading && (
        <div className="ai-admin-section">
          {editingPrompt ? (
            <div className="ai-admin-edit-form">
              <h3>Edit Prompt — {editingPrompt.feature_key} / {editingPrompt.prompt_type} (v{editingPrompt.version})</h3>
              <p className="ai-admin-hint">Saving creates a new version and deactivates the old one.</p>
              <textarea
                value={editPromptText}
                onChange={(e) => setEditPromptText(e.target.value)}
                rows={6}
              />
              <div className="ai-admin-edit-actions">
                <button className="ai-admin-btn-save" onClick={handleSavePrompt}>Save New Version</button>
                <button className="ai-admin-btn-cancel" onClick={() => setEditingPrompt(null)}>Cancel</button>
              </div>
            </div>
          ) : showAddPrompt ? (
            <div className="ai-admin-edit-form">
              <h3>Add New Prompt</h3>
              <div className="ai-admin-config-grid">
                <label>
                  <span>Feature Key</span>
                  <input type="text" placeholder="e.g. treatment-suggestions"
                    value={newPrompt.feature_key}
                    onChange={(e) => setNewPrompt({ ...newPrompt, feature_key: e.target.value })} />
                </label>
                <label>
                  <span>Prompt Type</span>
                  <select value={newPrompt.prompt_type}
                    onChange={(e) => setNewPrompt({ ...newPrompt, prompt_type: e.target.value })}>
                    <option value="system">system</option>
                    <option value="developer">developer</option>
                    <option value="user_template">user_template</option>
                  </select>
                </label>
              </div>
              <textarea
                placeholder="Enter prompt text..."
                value={newPrompt.prompt_text}
                onChange={(e) => setNewPrompt({ ...newPrompt, prompt_text: e.target.value })}
                rows={6}
              />
              <div className="ai-admin-edit-actions">
                <button className="ai-admin-btn-save" onClick={handleAddPrompt}>Create Prompt</button>
                <button className="ai-admin-btn-cancel" onClick={() => setShowAddPrompt(false)}>Cancel</button>
              </div>
            </div>
          ) : (
            <>
              <button className="ai-admin-btn-add" onClick={() => setShowAddPrompt(true)}
                style={{ marginBottom: 12 }}>
                + Add New Prompt
              </button>
              <table className="ai-admin-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>Type</th>
                  <th>Version</th>
                  <th>Active</th>
                  <th>Prompt Text</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {prompts.map((p) => (
                  <tr key={p.id} className={!p.is_active ? 'ai-admin-inactive' : ''}>
                    <td>{p.feature_key}</td>
                    <td>{p.prompt_type}</td>
                    <td>v{p.version}</td>
                    <td>{p.is_active ? '✅' : '—'}</td>
                    <td className="ai-admin-prompt-text" title={p.prompt_text}>
                      {p.prompt_text.length > 80 ? p.prompt_text.substring(0, 80) + '…' : p.prompt_text}
                    </td>
                    <td>
                      {p.is_active && (
                        <button className="ai-admin-btn-sm" onClick={() => handleEditPrompt(p)}>Edit</button>
                      )}
                    </td>
                  </tr>
                ))}
                {prompts.length === 0 && (
                  <tr><td colSpan="6" style={{ textAlign: 'center' }}>No prompts found. Click "Add New Prompt" to create one.</td></tr>
                )}
              </tbody>
            </table>
            </>
          )}
        </div>
      )}

      {/* ── Configs Tab ──────────────────────────────────────────────────── */}
      {activeTab === 'configs' && !loading && (
        <div className="ai-admin-section">
          {editingConfig ? (
            <div className="ai-admin-edit-form">
              <h3>Edit Config — {editingConfig.feature_key}</h3>
              <div className="ai-admin-config-grid">
                <label>
                  <span>Enabled</span>
                  <input type="checkbox" checked={editConfigValues.enabled}
                    onChange={(e) => setEditConfigValues({ ...editConfigValues, enabled: e.target.checked })} />
                </label>
                <label>
                  <span>Model</span>
                  <select value={editConfigValues.model}
                    onChange={(e) => setEditConfigValues({ ...editConfigValues, model: e.target.value })}>
                    <option value="gpt-4o-mini">gpt-4o-mini</option>
                    <option value="gpt-4.1-mini">gpt-4.1-mini</option>
                    <option value="gpt-4.1-nano">gpt-4.1-nano</option>
                    <option value="gpt-4o">gpt-4o</option>
                    <option value="gpt-5-mini">gpt-5-mini</option>
                    <option value="gpt-5.4-mini">gpt-5.4-mini</option>

                  </select>
                </label>
                <label>
                  <span>Temperature</span>
                  <input type="number" step="0.05" min="0" max="2" value={editConfigValues.temperature}
                    onChange={(e) => setEditConfigValues({ ...editConfigValues, temperature: parseFloat(e.target.value) })} />
                </label>
                <label>
                  <span>Max Output Tokens</span>
                  <input type="number" min="100" max="4096" value={editConfigValues.max_output_tokens}
                    onChange={(e) => setEditConfigValues({ ...editConfigValues, max_output_tokens: parseInt(e.target.value) })} />
                </label>
                <label>
                  <span>Rate Limit / Minute</span>
                  <input type="number" min="1" max="60" value={editConfigValues.rate_limit_per_minute}
                    onChange={(e) => setEditConfigValues({ ...editConfigValues, rate_limit_per_minute: parseInt(e.target.value) })} />
                </label>
                <label>
                  <span>Rate Limit / Day</span>
                  <input type="number" min="1" max="1000" value={editConfigValues.rate_limit_per_day}
                    onChange={(e) => setEditConfigValues({ ...editConfigValues, rate_limit_per_day: parseInt(e.target.value) })} />
                </label>
                <label>
                  <span>Lockout Duration (min)</span>
                  <input type="number" min="1" max="1440" value={editConfigValues.lockout_duration_minutes}
                    onChange={(e) => setEditConfigValues({ ...editConfigValues, lockout_duration_minutes: parseInt(e.target.value) })} />
                </label>
              </div>
              <div className="ai-admin-edit-actions">
                <button className="ai-admin-btn-save" onClick={handleSaveConfig}>Save</button>
                <button className="ai-admin-btn-cancel" onClick={() => setEditingConfig(null)}>Cancel</button>
              </div>
            </div>
          ) : (
            <table className="ai-admin-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>Enabled</th>
                  <th>Model</th>
                  <th>Temp</th>
                  <th>Max Tokens</th>
                  <th>Rate/Min</th>
                  <th>Rate/Day</th>
                  <th>Lockout (min)</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {configs.map((c) => (
                  <tr key={c.id}>
                    <td>{c.feature_key}</td>
                    <td>{c.enabled ? '✅' : '❌'}</td>
                    <td>{c.model}</td>
                    <td>{c.temperature}</td>
                    <td>{c.max_output_tokens}</td>
                    <td>{c.rate_limit_per_minute}</td>
                    <td>{c.rate_limit_per_day}</td>
                    <td>{c.lockout_duration_minutes}</td>
                    <td>
                      <button className="ai-admin-btn-sm" onClick={() => handleEditConfig(c)}>Edit</button>
                    </td>
                  </tr>
                ))}
                {configs.length === 0 && (
                  <tr><td colSpan="9" style={{ textAlign: 'center' }}>No configs found</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── Rate Limits Tab ──────────────────────────────────────────────── */}
      {activeTab === 'rate-limits' && !loading && (
        <div className="ai-admin-section">
          <button className="ai-admin-btn-sm" onClick={fetchRateLimits} style={{ marginBottom: 12 }}>
            Refresh
          </button>
          <table className="ai-admin-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Role</th>
                <th>Requests Today</th>
                <th>Requests/Min</th>
                <th>Locked Out</th>
                <th>Locked Until</th>
                <th>Reason</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {rateLimits.map((r) => (
                <tr key={r.user_id} className={r.is_locked ? 'ai-admin-locked' : ''}>
                  <td>{r.displayName || r.email}</td>
                  <td>{r.email}</td>
                  <td>{r.role}</td>
                  <td>{r.requests_today}</td>
                  <td>{r.requests_this_minute}</td>
                  <td>{r.is_locked ? '🔒 Yes' : 'No'}</td>
                  <td>{r.locked_until ? new Date(r.locked_until).toLocaleString() : '—'}</td>
                  <td>{r.lockout_reason || '—'}</td>
                  <td>
                    {r.is_locked && (
                      <button className="ai-admin-btn-sm ai-admin-btn-unlock" onClick={() => handleUnlockUser(r.lockout_id)}>
                        Unlock
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {rateLimits.length === 0 && (
                <tr><td colSpan="9" style={{ textAlign: 'center' }}>No AI usage today</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AiAdmin;
